'use client'

/**
 * Typed accessor for the Synop desktop shell bridge (`window.synopDesktop`),
 * exposed by synop-desktop-app's preload script. `undefined` in a browser —
 * always feature-detect through {@link getSynopDesktop}.
 *
 * The shell only speaks *intent* (nav/tab commands, routes to open); the web
 * app decides what each command means. Members marked optional ship in newer
 * shell builds than the original bridge — guard each call site.
 */

export type DesktopNavCommand = 'back' | 'forward'

export type DesktopTabCommand = 'close-tab' | 'new-tab' | 'next-tab' | 'previous-tab'

export type FleetHealthLevel = 'critical' | 'ok' | 'unknown' | 'warning'

export type DesktopNavigationState = {
  canGoBack: boolean
  canGoForward: boolean
  /** How many app tabs are open — the shell closes the window on Cmd+W at 1. */
  tabCount: number
}

export type DesktopContext = {
  appUrl: string
  /** Random per-installation id (desktop ≥ 0.1.6); tags the shell's own telemetry too. */
  installId?: string
  version: string
  electron: string
  chrome: string
  platform: string
}

export type SynopDesktopBridge = {
  isDesktop: true
  getContext: () => Promise<DesktopContext>
  setBadgeCount: (count: number) => Promise<boolean>
  openExternal: (url: string) => Promise<boolean>
  /** Marks the page as desktop-chrome-aware; call whenever any field changes. */
  setNavigationState?: (state: DesktopNavigationState) => Promise<boolean>
  /** Drives the menu-bar tray icon (colored dot + active-issue count). */
  setFleetHealth?: (health: { level: FleetHealthLevel; count: number }) => Promise<boolean>
  /** Menu accelerators (Cmd+[ / Cmd+]), trackpad swipes, mouse buttons. Returns unsubscribe. */
  onNavCommand?: (callback: (command: DesktopNavCommand) => void) => () => void
  /** Tab commands (Cmd+W with multiple tabs, future Cmd+T). Returns unsubscribe. */
  onTabCommand?: (callback: (command: DesktopTabCommand) => void) => () => void
  /** App-relative route from a `synop://` deep link or intercepted window.open. Returns unsubscribe. */
  onOpenRoute?: (callback: (route: string) => void) => () => void
}

/**
 * Minimal slice of the Navigation API (`window.navigation`) — present in the
 * Chromium the desktop shell embeds, but not yet in TypeScript's DOM lib.
 * Used for live `canGoBack`/`canGoForward`; traversal itself goes through
 * `window.history`, which the Next.js App Router already listens to.
 */
export type NavigationApiLike = {
  canGoBack: boolean
  canGoForward: boolean
  addEventListener: (type: 'currententrychange', listener: () => void) => void
  removeEventListener: (type: 'currententrychange', listener: () => void) => void
}

type WindowWithDesktopGlobals = Window & {
  synopDesktop?: SynopDesktopBridge
  navigation?: NavigationApiLike
}

export function getSynopDesktop(): SynopDesktopBridge | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as WindowWithDesktopGlobals).synopDesktop
}

export function getNavigationApi(): NavigationApiLike | undefined {
  if (typeof window === 'undefined') return undefined
  return (window as WindowWithDesktopGlobals).navigation
}
